import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuthToken } from '@/contexts/AuthContext';
import { usePlanFeatures } from '@/hooks/usePlanFeatures';
import axiosInstance from '@/utils/appsettings';
import {
    ENGAGEMENT_MIN_HOURS,
    SERVICE_COSTS,
    formatHourPrice,
    formatUSD,
} from '@/utils/expertHours';
import type { EngageServiceKey } from '@/components/engagement/engageServiceCopy';
import { isCoBuild10Plan } from '@/components/billing/pricingCopy';

export type ServiceBadgeTone = 'neutral' | 'positive' | 'muted';

export interface ServiceBadge {
    text: string;
    tone: ServiceBadgeTone;
}

export interface EngageService {
    key: EngageServiceKey;
    title: string;
    /** Full description, for the cards where there is room to read. */
    blurb: string;
    /** One short line, for the dropdown menu where vertical space is tight. */
    tagline: string;
    /** Headline price for the menu. Stated explicitly rather than inferred from badges. */
    priceLabel: string;
    priceTone: ServiceBadgeTone;
    badges: ServiceBadge[];
    enabled: boolean;
    /** Shown when disabled, explaining what would unlock the service. */
    disabledReason?: string;
    /** Already covered by the plan, so there is nothing to buy. */
    includedInPlan?: boolean;
}

/** Matches the plan-name spellings used across billing components. */
function isCoBuildPlan(planName?: string): boolean {
    if (!planName) return false;
    // Co-Build 10 is one month of hours on Starter; Production Support Advanced is not bundled with it
    if (isCoBuild10Plan(planName)) return false;
    const lower = planName.toLowerCase();
    return lower.includes('co-build') || lower.includes('cobuild') || lower.includes('co_build');
}

interface PublishedAppLite {
    project_id: string;
    status: string;
}

/**
 * Resolves which expert services to offer, and in what state, from signals the client
 * already has: the architect-hours balance, the project's platform, whether the project (or
 * account) has a running published app, and whether the plan already bundles production cover.
 *
 * Production cover requires the app to actually be live — someone preparing for launch can't
 * buy it yet — and it is web-only, because mobile apps ship through the app stores and the
 * platform never publishes or monitors them.
 */
export function useEngageServices(opts: {
    projectId?: string;
    balanceHours: number;
    isGithubImport?: boolean;
    /** Mobile apps ship through the app stores, so production cover does not apply to them. */
    isMobile?: boolean;
}) {
    const { projectId, balanceHours, isGithubImport, isMobile } = opts;
    const token = useAuthToken();
    const { planName } = usePlanFeatures();
    const isCoBuild = isCoBuildPlan(planName);

    // Production support only makes sense once something is actually live. Scoped to the
    // project when we have one, otherwise account-wide for the global entry points.
    const { data: liveApps, isLoading: loadingLive } = useQuery({
        queryKey: ['engage-live-apps', projectId ?? 'account'],
        queryFn: async () => {
            if (projectId) {
                const res = await axiosInstance.get(
                    `/app-publishing/projects/${projectId}/existing-app`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                const app = res.data?.app as PublishedAppLite | null | undefined;
                return app ? [app] : [];
            }
            const res = await axiosInstance.get('/app-publishing/apps', {
                params: { page: 1, limit: 100, status_filter: 'running' },
                headers: { Authorization: `Bearer ${token}` },
            });
            return (res.data?.apps || []) as PublishedAppLite[];
        },
        enabled: !!token,
        staleTime: 60_000,
        // A missing or failed publish lookup must not block the whole chooser.
        retry: false,
    });

    const hasLiveApp = useMemo(
        () => (liveApps || []).some((a) => a.status === 'running'),
        [liveApps]
    );

    const canRedeemDevelopment = balanceHours >= ENGAGEMENT_MIN_HOURS;

    const services = useMemo<EngageService[]>(() => {
        const development: EngageService = {
            key: 'development',
            title: 'Development support',
            blurb: 'An architect picks up what the AI could not finish. Deployment, auth, payments, scaling, or the bug you have been circling for three days.',
            tagline: 'Deploys, auth, payments, or a stubborn bug',
            priceLabel: canRedeemDevelopment
                ? `${Math.floor(balanceHours)}h ready`
                : `From ${formatHourPrice(ENGAGEMENT_MIN_HOURS)}`,
            priceTone: canRedeemDevelopment ? 'positive' : 'neutral',
            badges: canRedeemDevelopment
                ? [
                    { text: `${Math.floor(balanceHours)}h available`, tone: 'positive' },
                    { text: 'Architect hours', tone: 'neutral' },
                ]
                : [
                    { text: 'Architect hours', tone: 'neutral' },
                    { text: `From ${formatHourPrice(ENGAGEMENT_MIN_HOURS)}`, tone: 'neutral' },
                ],
            enabled: true,
        };

        const migration: EngageService = {
            key: 'migration',
            title: 'Migration support',
            blurb: 'Bring a project you built on Lovable, Replit, Bolt, v0 or anywhere else. We port it, wire it up and hand it back running on Joylo.',
            tagline: 'Move a project built somewhere else onto Joylo',
            priceLabel: formatUSD(SERVICE_COSTS.migration.displayUSD),
            priceTone: 'neutral',
            badges: [
                { text: 'Fixed price', tone: 'neutral' },
                { text: formatUSD(SERVICE_COSTS.migration.displayUSD), tone: 'neutral' },
            ],
            enabled: true,
        };

        const production: EngageService = {
            key: 'production',
            title: 'Production support',
            blurb: 'Your app is live and people depend on it. We watch uptime, database health and security, and step in when something breaks. Web apps only.',
            tagline: 'Uptime, database health, incident response',
            priceLabel: isCoBuild
                ? 'Included'
                : `From ${formatUSD(SERVICE_COSTS.production_essential.displayUSD)}/mo`,
            priceTone: isCoBuild ? 'positive' : 'neutral',
            badges: isCoBuild
                ? [
                    { text: 'Included with Co-Build', tone: 'positive' },
                    { text: 'Set up per app', tone: 'neutral' },
                ]
                : [
                    { text: 'Monthly', tone: 'neutral' },
                    { text: `From ${formatUSD(SERVICE_COSTS.production_essential.displayUSD)}/mo`, tone: 'neutral' },
                ],
            // Gated on platform (mobile apps are never published or monitored here) AND on
            // the app actually being live (or the plan already bundling cover).
            enabled: !isMobile && (isCoBuild || hasLiveApp),
            includedInPlan: isCoBuild,
            disabledReason: isMobile
                ? 'Covers web apps on Joylo. Mobile apps ship through the app stores.'
                : hasLiveApp
                    ? undefined
                    : 'Available once your app is published and running.',
        };

        // A GitHub-imported project is the one case where migration is the likely intent.
        return isGithubImport
            ? [migration, development, production]
            : [development, migration, production];
    }, [balanceHours, canRedeemDevelopment, hasLiveApp, isCoBuild, isGithubImport, isMobile]);

    return {
        services,
        isCoBuild,
        hasLiveApp,
        canRedeemDevelopment,
        loading: loadingLive,
    };
}
